/**
 * useRefreshWithTimestamp - Hook reutilizável para refresh com timestamp
 * 
 * Gerencia estado de refresh e formatação do tempo decorrido desde
 * a última atualização de dados.
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { useState, useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Interface de retorno do hook
 */
interface UseRefreshWithTimestampReturn {
  /** Data/hora da última atualização */
  lastUpdate: Date;
  /** Indica se está em processo de refresh */
  isRefreshing: boolean;
  /** Tempo formatado desde última atualização (ex: "há 2 min") */
  formattedTime: string;
  /** Executa refresh e atualiza timestamp */
  triggerRefresh: (refreshFn: () => Promise<void>) => Promise<void>;
}

/**
 * Hook para gerenciar refresh de dados com timestamp de última atualização
 * 
 * @returns Objeto com estado e funções de refresh
 * 
 * @example
 * const { isRefreshing, formattedTime, triggerRefresh } = useRefreshWithTimestamp();
 * 
 * const handleRefresh = async () => {
 *   await triggerRefresh(async () => {
 *     await queryClient.invalidateQueries({ queryKey: ['my-data'] });
 *   });
 * };
 */
export function useRefreshWithTimestamp(): UseRefreshWithTimestampReturn {
  // Data da última atualização
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  
  // Estado de refresh em andamento
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Formata tempo desde última atualização em português
  const formattedTime = formatDistanceToNow(lastUpdate, {
    addSuffix: true,
    locale: ptBR,
  });

  // Executa função de refresh e atualiza timestamp ao concluir
  const triggerRefresh = useCallback(async (refreshFn: () => Promise<void>) => {
    setIsRefreshing(true);
    try {
      await refreshFn();
      setLastUpdate(new Date());
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  return { lastUpdate, isRefreshing, formattedTime, triggerRefresh };
}
